import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import WebApp from '@twa-dev/sdk';
import { useGameContext } from './GameContext';

const TerminalContainer = styled.div`
  background-color: #121212;
  color: #00FF00;
  font-family: 'IBM Plex Mono', monospace;
  min-height: 100vh;
  padding: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      rgba(18, 16, 16, 0) 50%, 
      rgba(0, 0, 0, 0.25) 50%
    );
    background-size: 100% 4px;
    pointer-events: none;
    z-index: 1;
  }
`;

const TerminalHeader = styled.div`
  margin-bottom: 20px;
  color: #9C27B0;
  text-align: center;
`;

const OutputArea = styled.div`
  margin-bottom: 20px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const OutputLine = styled.div`
  margin-bottom: 8px;
  line-height: 1.4;
  color: ${props => {
    switch(props.type) {
      case 'error': return '#FF0000';
      case 'system': return '#9C27B0';
      case 'user': return '#FFFFFF';
      default: return '#00FF00';
    }
  }};
`;

const InputArea = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const Prompt = styled.span`
  color: #9C27B0;
  margin-right: 8px;
`;

const InputField = styled.input`
  background-color: transparent;
  border: none;
  color: #FFFFFF;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 16px;
  outline: none;
  width: 100%;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 10px;
  height: 18px;
  background-color: #00FF00;
  margin-left: 4px;
  animation: blink 1s step-end infinite;
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Интерфейс терминала
const Terminal = () => {
  const [output, setOutput] = useState([]);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const outputEndRef = useRef(null);
  
  // Используем контекст игры
  const {
    gameState,
    loadProgress,
    addToInventory,
    unlockCommand,
    advanceMission,
    addToLogs,
    discoverSecret,
    resetGame
  } = useGameContext();

  // Прокрутка вниз при добавлении нового вывода
  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [output]);

  // Добавление новой строки вывода
  const addOutput = (text, type = 'default') => {
    setOutput(prev => [...prev, { text, type }]);
    
    // Также добавляем важные сообщения в лог игры
    if (type !== 'user') {
      addToLogs({ message: text, type });
    }
  };
  
  // Определяем showWelcomeMessage перед useEffect, чтобы избежать проблем с зависимостями
  const showWelcomeMessage = () => {
    setIsTyping(true);
    
    addOutput('ИНИЦИАЛИЗАЦИЯ ТЕРМИНАЛА...', 'system');
    
    setTimeout(() => {
      addOutput(`
 ██████╗██╗██████╗ ██╗  ██╗███████╗██████╗     
██╔════╝██║██╔══██╗██║  ██║██╔════╝██╔══██╗    
██║     ██║██████╔╝███████║█████╗  ██████╔╝    
██║     ██║██╔═══╝ ██╔══██║██╔══╝  ██╔══██╗    
╚██████╗██║██║     ██║  ██║███████╗██║  ██║    
 ╚═════╝╚═╝╚═╝     ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝    
                                               
██╗  ██╗██╗   ██╗███╗   ██╗████████╗           
██║  ██║██║   ██║████╗  ██║╚══██╔══╝           
███████║██║   ██║██╔██╗ ██║   ██║              
██╔══██║██║   ██║██║╚██╗██║   ██║              
██║  ██║╚██████╔╝██║ ╚████║   ██║              
╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═══╝   ╚═╝              
`, 'system');
    }, 1000);

    setTimeout(() => {
      addOutput('Добро пожаловать, агент. Вы подключены к защищенному терминалу TERM Protocol.', 'system');
    }, 2000);

    setTimeout(() => {
      addOutput('ВНИМАНИЕ: Обнаружена попытка компрометации узла [REDACTED].', 'error');
      addOutput('Все системные администраторы мертвы.', 'error');
      addOutput('Вы — наша последняя надежда.', 'error');
    }, 3000);

    setTimeout(() => {
      addOutput('Ваша задача: расшифровать перехваченное сообщение, установить источник утечки и предотвратить полную компрометацию системы.', 'system');
    }, 4000);

    setTimeout(() => {
      addOutput('Введите "help" для списка доступных команд или "start" для начала расследования.', 'system');
      setIsTyping(false);
    }, 5000);
  };

  // Инициализация Telegram WebApp и загрузка состояния игры
  useEffect(() => {
    WebApp.ready();
    WebApp.expand();
    
    // Пытаемся загрузить сохраненный прогресс
    const progressLoaded = loadProgress();
    
    if (!progressLoaded) {
      // Если прогресс не загружен - показываем приветственное сообщение
      showWelcomeMessage();
    } else {
      // Если прогресс загружен - показываем сообщение о восстановлении сессии
      addOutput('Сессия восстановлена. Продолжайте расследование.', 'system');
      addOutput(`Текущая миссия: ${gameState.mission}, Прогресс: ${gameState.progress}%`, 'system');
      addOutput('Введите "status" для информации о текущем статусе или "help" для списка команд.', 'system');
    }
    
    // Фокус на поле ввода
    inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Используем eslint-disable, так как мы осознанно не хотим перезапускать эффект при изменении зависимостей

  // Функция для отслеживания ключевых действий (для простоты сохраняем в localStorage)
  const trackEvent = (category, action, label = '') => {
    try {
      // Создаем или получаем существующую статистику из localStorage
      const stats = JSON.parse(localStorage.getItem('cipher_hunt_stats') || '{"events":[]}');
      
      // Добавляем новое событие
      stats.events.push({
        category,
        action,
        label,
        timestamp: new Date().toISOString(),
        sessionId: localStorage.getItem('session_id') || 'unknown'
      });
      
      // Сохраняем статистику обратно
      localStorage.setItem('cipher_hunt_stats', JSON.stringify(stats));
      
      // Для дебага в консоль (можно убрать в production)
      console.log(`Event tracked: ${category} - ${action} - ${label}`);
    } catch (e) {
      console.error('Error tracking event:', e);
    }
  };

  // Создаем уникальный ID сессии при первом запуске
  useEffect(() => {
    if (!localStorage.getItem('session_id')) {
      localStorage.setItem('session_id', Date.now().toString(36) + Math.random().toString(36).substr(2));
      trackEvent('System', 'Session Start', 'New User');
    } else {
      trackEvent('System', 'Session Start', 'Returning User');
    }
  }, []);

  // Обработка ввода команды
  const handleCommand = (cmd) => {
    const command = cmd.trim().toLowerCase();
    
    // Добавляем введенную команду в историю
    setHistory(prev => [command, ...prev].slice(0, 10));
    setHistoryIndex(-1);
    
    // Отображаем введенную команду
    addOutput(`> ${command}`, 'user');
    
    // Отслеживаем использование команды
    trackEvent('Command', 'Execute', command.split(' ')[0]);
    
    // Проверяем, разблокирована ли команда
    const firstWord = command.split(' ')[0];
    
    // Специальная обработка для команды feedback, которая всегда доступна
    if (firstWord === 'feedback') {
      handleFeedback(command.substring(9));
      return;
    }
    
    if (!gameState.unlockedCommands.includes(firstWord) && firstWord !== 'reset') {
      addOutput(`Команда "${firstWord}" не распознана или недоступна на текущем уровне доступа.`, 'error');
      return;
    }
    
    // Обработка разных команд
    switch(firstWord) {
      case 'help':
        addOutput(`Доступные команды:
${gameState.unlockedCommands.includes('help') ? '- help: Показать список доступных команд' : ''}
${gameState.unlockedCommands.includes('about') ? '- about: Информация о проекте' : ''}
${gameState.unlockedCommands.includes('start') ? '- start: Начать расследование' : ''}
${gameState.unlockedCommands.includes('clear') ? '- clear: Очистить терминал' : ''}
${gameState.unlockedCommands.includes('decrypt') ? '- decrypt [message]: Попытаться расшифровать сообщение' : ''}
${gameState.unlockedCommands.includes('analyze') ? '- analyze [data]: Проанализировать данные' : ''}
${gameState.unlockedCommands.includes('status') ? '- status: Показать текущий статус расследования' : ''}
${gameState.unlockedCommands.includes('inventory') ? '- inventory: Показать ваш инвентарь' : ''}
- feedback [сообщение]: Отправить отзыв разработчикам`);
        break;
        
      case 'about':
        addOutput(`CIPHER://HUNT — интерактивная криптографическая игра-расследование, разработанная как часть экосистемы Retarded Cafe. 
        
Игра сочетает образовательные элементы с увлекательным геймплеем, обучая принципам цифрового суверенитета и приватности.`);
        break;
        
      case 'start':
        startMission();
        break;
        
      case 'clear':
        setOutput([]);
        break;
        
      case 'status':
        showStatus();
        break;
        
      case 'inventory':
        showInventory();
        break;
        
      case 'reset':
        // Секретная команда для разработки
        resetGame();
        setOutput([]);
        showWelcomeMessage();
        break;
        
      case 'community':
        handleCommunity();
        break;
        
      default:
        if (command.startsWith('decrypt ')) {
          const message = command.substring(8);
          decryptMessage(message);
        } else if (command.startsWith('analyze ')) {
          const data = command.substring(8);
          analyzeData(data);
        } else {
          addOutput(`Команда "${command}" не распознана. Введите "help" для списка доступных команд.`, 'error');
        }
    }
  };

  // Показать статус игры
  const showStatus = () => {
    addOutput(`Текущий статус расследования:`, 'system');
    addOutput(`Миссия: ${gameState.mission}`, 'default');
    addOutput(`Прогресс: ${gameState.progress}%`, 'default');
    addOutput(`Открытые команды: ${gameState.unlockedCommands.join(', ')}`, 'default');
    
    if (gameState.mission === 0 && gameState.progress === 0) {
      addOutput('Расследование еще не начато. Введите "start" для начала.', 'system');
    }
  };
  
  // Показать инвентарь
  const showInventory = () => {
    if (gameState.inventory.length === 0) {
      addOutput('Ваш инвентарь пуст.', 'system');
    } else {
      addOutput('Ваш инвентарь:', 'system');
      gameState.inventory.forEach(item => {
        addOutput(`- ${item.name}: ${item.description}`, 'default');
      });
    }
  };

  // Запуск миссии
  const startMission = () => {
    setIsTyping(true);
    
    // Обновляем состояние игры
    advanceMission(1, 10);
    
    // Разблокируем необходимые команды
    unlockCommand('decrypt');
    
    addOutput('Инициализация первой миссии...', 'system');
    
    setTimeout(() => {
      addOutput('ПЕРЕХВАЧЕННОЕ СООБЩЕНИЕ:', 'system');
      addOutput('Wkh dwwdfn zloo ehjlq dw plgqljkw. Ghvwur| doo hylghqfh.', 'default');
      addOutput('Сообщение зашифровано с использованием простого шифра сдвига.', 'system');
      addOutput('Используйте команду "decrypt" для попытки расшифровки.', 'system');
      setIsTyping(false);
    }, 2000);
  };

  // Расшифровка сообщения
  const decryptMessage = (message) => {
    if (message === 'wkh dwwdfn zloo ehjlq dw plgqljkw. ghvwur| doo hylghqfh.' ||
        message === 'Wkh dwwdfn zloo ehjlq dw plgqljkw. Ghvwur| doo hylghqfh.') {
      addOutput('Расшифровка...', 'system');
      
      // Обновляем прогресс игры
      advanceMission(1, 50);
      
      // Разблокируем команду анализа
      unlockCommand('analyze');
      
      // Отслеживаем успешную расшифровку
      trackEvent('Game', 'Decrypt Success', message.substring(0, 20));
      
      // Добавляем инструмент расшифровки в инвентарь
      if (!gameState.inventory.some(item => item.id === 'decryptor')) {
        addToInventory({
          id: 'decryptor',
          name: 'Дешифратор',
          description: 'Базовый инструмент для расшифровки сообщений с использованием классических алгоритмов.'
        });
        
        // Разблокируем команду инвентаря
        unlockCommand('inventory');
      }
      
      setTimeout(() => {
        addOutput('УСПЕХ! Сообщение расшифровано:', 'system');
        addOutput('The attack will begin at midnight. Destroy all evidence.', 'default');
        addOutput('Используйте команду "analyze" с ключевыми словами из сообщения для продолжения расследования.', 'system');
        
        // Регистрируем найденный секрет
        discoverSecret('message1', {
          plaintext: 'The attack will begin at midnight. Destroy all evidence.',
          ciphertext: 'Wkh dwwdfn zloo ehjlq dw plgqljkw. Ghvwur| doo hylghqfh.',
          algorithm: 'Caesar Cipher',
          key: 3
        });
      }, 1500);
    } else if (message) {
      // Отслеживаем неудачную попытку
      trackEvent('Game', 'Decrypt Failure', message.substring(0, 20));
      addOutput('Не удалось расшифровать сообщение. Проверьте введенные данные и попробуйте снова.', 'error');
    } else {
      addOutput('Укажите сообщение для расшифровки. Например: "decrypt Wkh dwwdfn zloo ehjlq dw plgqljkw"', 'system');
    }
  };

  // Анализ данных
  const analyzeData = (data) => {
    // Отслеживаем попытку анализа
    trackEvent('Game', 'Analyze', data);
    
    if (data.includes('midnight') || data.includes('attack')) {
      addOutput('Анализирую данные о возможном времени атаки...', 'system');
      
      // Обновляем прогресс
      advanceMission(1, 75);
      
      setTimeout(() => {
        addOutput('ОБНАРУЖЕНО: Активность в системе перед полуночью показывает подготовку к компрометации главных узлов.', 'system');
        addOutput('Необходимо установить защитный периметр до полуночи.', 'system');
        addOutput('Это был лишь первый шаг расследования. Полная версия игры будет содержать более сложные головоломки и разветвленный сюжет.', 'system');
        
        // Завершаем миссию
        advanceMission(1, 100);
        
        // Отслеживаем завершение миссии
        trackEvent('Game', 'Mission Complete', 'Mission 1');
      }, 2000);
    } else if (data.includes('evidence') || data.includes('destroy')) {
      addOutput('Анализирую данные об уничтожении улик...', 'system');
      
      // Обновляем прогресс
      advanceMission(1, 75);
      
      setTimeout(() => {
        addOutput('ОБНАРУЖЕНО: Система логирования была скомпрометирована. Резервные копии журналов были удалены.', 'system');
        addOutput('Восстановление данных возможно через узел "Архив-7".', 'system');
        addOutput('Это был лишь первый шаг расследования. Полная версия игры будет содержать более сложные головоломки и разветвленный сюжет.', 'system');
        
        // Завершаем миссию
        advanceMission(1, 100);
      }, 2000);
    } else {
      addOutput(`Анализ "${data}" не дал значимых результатов. Попробуйте проанализировать ключевые слова из расшифрованного сообщения.`, 'error');
    }
  };

  // Обработка ввода
  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  // Обработка нажатия клавиш
  const handleKeyDown = (e) => {
    if (isTyping) return; // Не обрабатываем ввод во время анимации печати
    
    if (e.key === 'Enter' && input.trim()) {
      handleCommand(input);
      setInput('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length > 0 && historyIndex < history.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(history[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(history[newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  // Обработка команды feedback
  const handleFeedback = (message) => {
    if (!message || message.trim() === '') {
      addOutput('Пожалуйста, добавьте текст сообщения. Пример: feedback Отличная игра!', 'system');
      return;
    }
    
    // Сохраняем отзыв в localStorage
    try {
      const feedback = JSON.parse(localStorage.getItem('cipher_hunt_feedback') || '[]');
      feedback.push({
        message: message.trim(),
        timestamp: new Date().toISOString(),
        sessionId: localStorage.getItem('session_id') || 'unknown'
      });
      localStorage.setItem('cipher_hunt_feedback', JSON.stringify(feedback));
      
      // Отслеживаем событие
      trackEvent('Feedback', 'Submit', message.substring(0, 30));
      
      // Генерируем ссылку для открытия чата и отправки сообщения разработчику
      const telegramLink = `https://t.me/+qsQrV2v2sBsyMzJl`;
      
      // В будущем можно заменить на реальный ID канала или группы
      addOutput('Спасибо за ваш отзыв! Он сохранен и будет передан разработчикам.', 'system');
      addOutput(`Вы также можете присоединиться к сообществу CIPHER://HUNT: ${telegramLink}`, 'system');
      
      // Предлагаем WebApp.openTelegramLink, если доступно
      if (typeof WebApp.openTelegramLink === 'function') {
        addOutput('Нажмите для перехода в чат', 'system');
        // В будущем можно использовать WebApp.openTelegramLink(telegramLink);
      }
    } catch (e) {
      console.error('Error saving feedback:', e);
      addOutput('Произошла ошибка при сохранении отзыва. Пожалуйста, попробуйте позже.', 'error');
    }
  };

  function handleCommunity() {
    const communityLink = 'https://t.me/+qsQrV2v2sBsyMzJl';
    addOutput('Присоединяйтесь к сообществу CIPHER://HUNT!', 'system');
    addOutput(`Ссылка: ${communityLink}`, 'system');
    
    // Предлагаем WebApp.openTelegramLink, если доступно
    if (window.Telegram?.WebApp?.openTelegramLink) {
      addOutput('Нажмите здесь, чтобы открыть группу:', 'system');
      setTimeout(() => {
        const linkElement = document.createElement('a');
        linkElement.innerText = '→ Открыть в Telegram';
        linkElement.className = 'interactive-link';
        linkElement.addEventListener('click', () => {
          window.Telegram.WebApp.openTelegramLink(communityLink);
        });
        addOutput(linkElement, 'element');
      }, 100);
    }
    
    trackEvent('community_command_used');
  }

  return (
    <TerminalContainer onClick={() => inputRef.current.focus()}>
      <TerminalHeader>
        CIPHER://HUNT - Терминал защищенного доступа v1.0
      </TerminalHeader>
      
      <OutputArea>
        {output.map((line, index) => (
          <OutputLine key={index} type={line.type}>
            {line.text}
          </OutputLine>
        ))}
        <div ref={outputEndRef} />
      </OutputArea>
      
      {!isTyping && (
        <InputArea>
          <Prompt>{'>'}</Prompt>
          <InputField
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <Cursor />
        </InputArea>
      )}
    </TerminalContainer>
  );
};

export default Terminal; 